import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import DatabaseBackup from "@/components/DatabaseBackup";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, Users, CalendarDays, Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="border border-border bg-card p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground mb-3">
      <Icon size={16} strokeWidth={2} />
      <span className="text-xs tracking-[0.15em] uppercase font-medium">{label}</span>
    </div>
    <div className="font-heading font-black text-4xl tracking-tight mono">{value}</div>
  </div>
);

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [savingDept, setSavingDept] = useState(false);

  const load = async () => {
    const [s, d, u] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/departments"),
      api.get("/admin/users"),
    ]);
    setStats(s.data);
    setDepts(d.data.departments);
    setUsers(u.data.users);
  };

  useEffect(() => {
    if (user === null) return; // still checking auth
    if (user.role !== "admin") {
      navigate("/");
      return;
    }
    load().catch(() => toast.error("Gagal memuat panel admin"));
    // eslint-disable-next-line
  }, [user]);

  const addDept = async (e) => {
    e.preventDefault();
    if (!newDept.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/departments", { name: newDept.trim() });
      setNewDept("");
      toast.success("Departemen ditambahkan");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const removeDept = async (id) => {
    try {
      await api.delete(`/admin/departments/${id}`);
      toast.success("Departemen dihapus");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const editDept = async (e) => {
    e.preventDefault();
    if (!editingDept || !editedName.trim()) return;
    setSavingDept(true);
    try {
      await api.put(`/admin/departments/${editingDept.id}`, { name: editedName.trim() });
      toast.success("Nama departemen diperbarui");
      setEditingDept(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingDept(false);
    }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]">
        <div className="mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-primary font-medium mb-2 mono">
            <ShieldCheck size={14} /> Super Admin
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">Control Panel</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">Kelola departemen, lihat pengguna, dan ringkasan sistem.</p>
        </div>

        <DatabaseBackup />
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 sm:mb-10">
          <StatCard icon={CalendarDays} label="Total Rapat" value={stats?.meetings ?? "—"} testid="stat-meetings" />
          <StatCard icon={Building2} label="Departemen" value={stats?.departments ?? "—"} testid="stat-departments" />
          <StatCard icon={Users} label="Pengguna" value={stats?.users ?? "—"} testid="stat-users" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Departments manager */}
          <div className="lg:col-span-2 border border-border bg-card p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-5">
              <Building2 size={16} className="text-primary" />
              <h3 className="font-heading font-bold text-sm tracking-[0.15em] uppercase">Kelola Departemen</h3>
            </div>

            <form onSubmit={addDept} className="flex flex-col sm:flex-row gap-2 mb-5 pb-5 border-b border-border">
              <Input
                data-testid="new-department-input"
                value={newDept}
                onChange={(e) => setNewDept(e.target.value)}
                placeholder="Nama departemen baru…"
                className="rounded-none focus-visible:ring-2 focus-visible:ring-primary"
              />
              <Button
                type="submit"
                disabled={adding}
                data-testid="add-department-btn"
                className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
              >
                <Plus size={16} className="mr-2" /> Tambah
              </Button>
            </form>

            <div className="divide-y divide-border" data-testid="admin-departments-list">
              {depts.map((d) => (
                <div key={d.id} className="flex items-center gap-3 py-3" data-testid={`admin-dept-${d.id}`}>
                  <span className="flex-1 text-sm font-medium">{d.name}</span>
                  <Badge variant="outline" className="rounded-none mono text-[10px]">
                    {d.meeting_count} rapat
                  </Badge>
                  <button
                    type="button"
                    data-testid={`edit-dept-${d.id}`}
                    className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
                    title={`Edit ${d.name}`}
                    aria-label={`Edit departemen ${d.name}`}
                    onClick={() => { setEditingDept(d); setEditedName(d.name); }}
                  >
                    <Pencil size={16} />
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`delete-dept-${d.id}`}
                        className="p-2 text-muted-foreground hover:text-primary transition-colors duration-150"
                        title="Hapus"
                      >
                        <Trash2 size={16} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-none">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading tracking-tight">Hapus departemen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Departemen "{d.name}" akan dihapus dari daftar pilihan.
                          {d.meeting_count > 0 && ` Rapat yang sudah memakai departemen ini (${d.meeting_count}) tidak akan berubah.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Batal</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => removeDept(d.id)}
                          data-testid={`confirm-delete-dept-${d.id}`}
                          className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                          Hapus
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
              {depts.length === 0 && <p className="text-sm text-muted-foreground py-4">Belum ada departemen.</p>}
            </div>
          </div>

          <Dialog open={Boolean(editingDept)} onOpenChange={(open) => { if (!open && !savingDept) setEditingDept(null); }}>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading">Edit Departemen</DialogTitle>
                <DialogDescription>Nama departemen pada semua meeting terkait juga akan diperbarui.</DialogDescription>
              </DialogHeader>
              <form onSubmit={editDept} className="space-y-4">
                <div>
                  <label htmlFor="edit-department-name" className="block text-sm font-medium mb-2">Nama departemen</label>
                  <Input
                    id="edit-department-name"
                    data-testid="edit-department-input"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    required
                    className="rounded-none"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" disabled={savingDept} onClick={() => setEditingDept(null)} className="rounded-none">Batal</Button>
                  <Button type="submit" disabled={savingDept || !editedName.trim()} data-testid="save-department-btn" className="rounded-none">
                    {savingDept ? "Menyimpan..." : "Simpan"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Users list */}
          <div className="lg:col-span-1 border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-5">
              <Users size={16} className="text-primary" />
              <h3 className="font-heading font-bold text-sm tracking-[0.15em] uppercase">Pengguna</h3>
            </div>
            <div className="divide-y divide-border" data-testid="admin-users-list">
              {users.map((u) => (
                <div key={u.id} className="py-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{u.name}</span>
                    <Badge
                      variant={u.role === "admin" ? "default" : "outline"}
                      className={`rounded-none text-[10px] ${u.role === "admin" ? "bg-primary text-primary-foreground" : ""}`}
                    >
                      {u.role}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
